import { styled } from '@mui/material/styles'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'

import { SchemaRecording } from '@/__generated/es-api'
import AudioPlayer from '@/app/component/atom/audio-player'
import CampaignLink from '@/app/component/atom/campaign-link'
import CustomTooltip from '@/app/component/atom/tooltip'
import TableRowActions from '@/app/component/layout/table/actions'
import { formatDateTime } from '@/app/service/util'
import downloadService from '@/app/service/util/download'
import { formatName } from '@/app/service/util/format'

type Props = {
  downloading: boolean
  orgId: number
  recording: SchemaRecording
  onDownload: (recordingId: number) => void
}

const CallRecordingsTableRow: React.FC<Props> = ({ downloading, recording, orgId, onDownload }) => {
  const [audioFile, setAudioFile] = React.useState<string>() // We need to keep the file locally to play it
  const [audioLoading, setAudioLoading] = React.useState(false)

  const handlePlayToggle = React.useCallback(async () => {
    if (audioFile) {
      return
    }
    setAudioLoading(true)
    try {
      const file = await downloadService.recordings(orgId, recording.id, false)
      const audioUrl = URL.createObjectURL(file)
      setAudioFile(audioUrl)
    } finally {
      setAudioLoading(false)
    }
  }, [audioFile, orgId, recording.id])

  const date = formatDateTime(recording.callStart)
  const transcript = recording.transcript?.trim() || ''
  const isDeletedContact = recording.contactDeleted
  const contactName = isDeletedContact
    ? 'Deleted Contact'
    : formatName(recording.firstName, recording.lastName) || recording.fullPhoneNumber || '—'
  const userName = recording.userUsername
  return (
    <TableRow hover>
      <TableCell>{date}</TableCell>
      <TableCell>
        {isDeletedContact ? (
          <CustomTooltip title="Audio for deleted contacts is no longer available">
            <div>
              <AudioPlayer disabled playId={`${recording.id}`} />
            </div>
          </CustomTooltip>
        ) : (
          <AudioPlayer
            disabled={false}
            duration={recording.duration}
            loading={audioLoading}
            playId={`${recording.id}`}
            source={audioFile}
            onPlayToggle={handlePlayToggle}
          />
        )}
      </TableCell>
      <TranscriptTableCell>
        {transcript ? (
          <Tooltip title={transcript} placement="top-start" enterDelay={300} arrow>
            <TranscriptContainer>
              <TranscriptText>{transcript}</TranscriptText>
            </TranscriptContainer>
          </Tooltip>
        ) : (
          <NoTranscript>No transcript</NoTranscript>
        )}
      </TranscriptTableCell>
      <TableCell>
        <CampaignLink campaignId={recording.campaignId} campaignName={recording.campaignName} size="small" />
      </TableCell>
      <TableCell>{contactName}</TableCell>
      <TableCell>{userName}</TableCell>
      <TableCell align="center">
        <ActionsContainer>
          <TableRowActions
            config={[
              {
                icon: 'download',
                label: 'Download',
                disabled: downloading || isDeletedContact,
                tooltip: isDeletedContact ? 'Audio for deleted contacts is no longer available' : 'Download recording',
                onClick: () => onDownload(recording.id),
              },
            ]}
          />
        </ActionsContainer>
      </TableCell>
    </TableRow>
  )
}

const ActionsContainer = styled('div')({
  '& > div': {
    marginRight: 0,
  },
})

const TranscriptTableCell = styled(TableCell)({
  maxWidth: 320,
  width: 320,
})

const TranscriptContainer = styled('div')(({ theme }) => ({
  borderRadius: 4,
  padding: theme.spacing(1),
  fontSize: '0.75rem',
  maxWidth: 320,
  whiteSpace: 'pre-wrap',
}))

const TranscriptText = styled('div')({
  fontSize: '0.75rem',
  lineHeight: 1.4,
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
  overflowWrap: 'anywhere',
})

const NoTranscript = styled('span')(({ theme }) => ({
  fontSize: '0.75rem',
  fontStyle: 'italic',
  color: theme.palette.text.disabled,
}))

export default CallRecordingsTableRow
